import { ChangeDetectorRef, Component, ChangeDetectionStrategy, signal, input, output, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { plusIcon, SVGIcon, pencilIcon, trashIcon } from '@progress/kendo-svg-icons';
import { TypeSubtypeItemRowComponent } from './components/type-subtype-item-row/type-subtype-item-row.component';
import { SourceMappingDialogComponent } from './components/source-mapping-dialog/source-mapping-dialog.component';
import {
  ServiceTypeConfig,
  ServiceTypeMappingConfig,
  SourceMapping,
  createEmptyServiceTypeConfig,
} from '../../models/service-type-mapping.models';
import { DialogModule } from '@progress/kendo-angular-dialog';

/**
 * Service Type Mapping tab component.
 *
 * Unified workflow:
 * - Default Type/Sub-Type/Item configuration (applies to all tickets or as fallback)
 * - Optional source-specific custom mappings
 */
@Component({
  selector: 'app-service-type-mapping',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    SVGIconModule,
    TypeSubtypeItemRowComponent,
    SourceMappingDialogComponent,
    DialogModule,
  ],
  templateUrl: './service-type-mapping.component.html',
  styleUrls: ['./service-type-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceTypeMappingComponent {
  private cdr = inject(ChangeDetectorRef);

  // Inputs
  organizationId = input<number | null>(null);
  boardId = input<number | null>(null);
  initialConfig = input<ServiceTypeMappingConfig | null>(null);
  integrationDetailsPk = input<number | null>(null);

  // Output - emits complete config on any change
  configChanged = output<ServiceTypeMappingConfig>();

  // Icons
  public plusIcon: SVGIcon = plusIcon;
  public pencilIcon: SVGIcon = pencilIcon;
  public trashIcon: SVGIcon = trashIcon;

  // Default config state
  defaultConfig = signal<ServiceTypeConfig>(createEmptyServiceTypeConfig());

  // Source mappings state
  sourceMappings = signal<SourceMapping[]>([]);

  // Dialog state
  isDialogOpen = signal<boolean>(false);
  editingMapping = signal<SourceMapping | null>(null);
  isDeleteDialogOpen = signal<boolean>(false);
  deletingMapping = signal<SourceMapping | null>(null);

  // Computed: used sources for filtering dialog dropdown
  usedSources = computed(() => {
    return this.sourceMappings().map((m) => m.sourceValue);
  });

  // Computed: whether custom mappings exist (for dynamic helper text)
  hasCustomMappings = computed(() => this.sourceMappings().length > 0);

  // Computed: helper text changes based on whether custom mappings exist
  helperText = computed(() => {
    if (this.hasCustomMappings()) {
      return 'Fallback for tickets that don\'t match a custom source mapping below. All fields are optional.';
    }
    return 'This configuration applies to all tickets. All fields are optional.';
  });

  constructor() {
    this.initConfigEffect();
  }

  /**
   * Effect to sync initialConfig input with internal state
   */
  private initConfigEffect(): void {
    effect(
      () => {
        const config = this.initialConfig();
        if (config) {
          this.defaultConfig.set(config.defaultConfig);
          this.sourceMappings.set([...config.mappings]);
          this.cdr.detectChanges();
        }
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Handle default config change
   */
  onDefaultConfigChanged(config: ServiceTypeConfig): void {
    this.defaultConfig.set(config);
    this.emitCurrentConfig();
  }

  /**
   * Open add mapping dialog
   */
  openAddDialog(): void {
    this.editingMapping.set(null);
    this.isDialogOpen.set(true);
  }

  /**
   * Open edit mapping dialog
   */
  openEditDialog(mapping: SourceMapping): void {
    this.editingMapping.set(mapping);
    this.isDialogOpen.set(true);
  }

  /**
   * Close mapping dialog
   */
  closeDialog(): void {
    this.isDialogOpen.set(false);
    this.editingMapping.set(null);
  }

  /**
   * Handle mapping saved from dialog
   */
  onMappingSaved(mapping: SourceMapping): void {
    const currentMappings = this.sourceMappings();
    const existingIndex = currentMappings.findIndex((m) => m.sourceValue === this.editingMapping()?.sourceValue);

    if (existingIndex >= 0) {
      // Update existing mapping
      const updated = [...currentMappings];
      updated[existingIndex] = mapping;
      this.sourceMappings.set(updated);
    } else {
      // Add new mapping
      this.sourceMappings.set([...currentMappings, mapping]);
    }

    this.closeDialog();
    this.emitCurrentConfig();
  }

  /**
   * Open delete confirmation dialog
   */
  openDeleteDialog(mapping: SourceMapping): void {
    this.deletingMapping.set(mapping);
    this.isDeleteDialogOpen.set(true);
  }

  /**
   * Close delete dialog
   */
  closeDeleteDialog(): void {
    this.isDeleteDialogOpen.set(false);
    this.deletingMapping.set(null);
  }

  /**
   * Confirm delete mapping
   */
  confirmDelete(): void {
    const toDelete = this.deletingMapping();
    if (toDelete) {
      const updated = this.sourceMappings().filter((m) => m.sourceValue !== toDelete.sourceValue);
      this.sourceMappings.set(updated);
      this.emitCurrentConfig();
    }
    this.closeDeleteDialog();
  }

  /**
   * Emit current configuration
   */
  private emitCurrentConfig(): void {
    const config: ServiceTypeMappingConfig = {
      defaultConfig: this.defaultConfig(),
      mappings: this.sourceMappings(),
    };

    this.configChanged.emit(config);
  }

  /**
   * Format the Type/Sub-Type portion of the config display
   */
  formatTypeDisplay(config: ServiceTypeConfig): string {
    const parts: string[] = [];
    if (config.type) parts.push(config.type.display_text);
    if (config.subtype) parts.push(config.subtype.display_text);
    return parts.length > 0 ? parts.join(' → ') : '';
  }

  /**
   * Format the Item portion of the config display
   */
  formatItemDisplay(config: ServiceTypeConfig): string {
    return config.item?.display_text ?? '';
  }
}
